import { Controller, FormContainer, useFormContext } from 'react-hook-form-mui';
import { Button, RadioGroup, /* Select, */ Spinner } from 'view/components';
import IndustryDropdownAutocomplete from 'view/pages/profile/components/IndustryDropdownAutocomplete';
import { SearchAutocompleteField } from 'view/pages/profile/components';
import { DropdownsEnum } from 'infrastructure/enums/DropdownsEnum';
import { useGetDropdownData } from 'view/pages/profile/hooks/useGetDropdownData';
import { useGetRecruiterPreferences } from 'view/pages/profile/hooks/useGetRecruiterPreferences';
import { useUpdateRecruiterPreferences } from 'view/pages/profile/hooks/useUpdateRecruiterPreferences';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import * as S from 'view/pages/profile/WorkPreferences/styled';
import { useContextSelector as useCS } from 'use-context-selector';
import { ProfileConfigContext } from 'domain/profileConfig/ProfileConfigContext';
import { profileConfigSelectors } from 'domain/profileConfig/selectors';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import TestSelect from 'view/pages/profile/TestPage/Select';

const hiringNeedsOptions = [
  { value: 'representative', label: 'I am representing a company' },
  { value: 'individual', label: 'I am Hiring independently' },
];

interface IFormInput {
  jobTitle: null;
  industry: null;
  recruiterType: string;
  company?: any;
}

const RecruiterPreferencesFields = ({
  isUpdatingRP,
  jobCategoryData,
  isJobCategoryDataLoading,
}) => {
  const { watch, setValue, control } = useFormContext<IFormInput>();
  const industryValue = watch('industry') ?? null;
  const hiringNeedsValue = watch('recruiterType') ?? false;
  const companyValue = watch('company') ?? null;

  return (
    <>
      <IndustryDropdownAutocomplete industryValue={industryValue} setValue={setValue} />
      <Controller
        name="jobTitle"
        control={control}
        render={({ field }) => {
          return (
            <TestSelect
              {...field}
              label="Job Title / Role"
              options={jobCategoryData?.[0]?.titles ?? []}
              selectedValue={field.value}
              onChange={(selected) => {
                return field.onChange(selected);
              }}
              disabled={isJobCategoryDataLoading || !jobCategoryData}
            />
          );
        }}
      />
      <S.TextWrapper>
        <S.TextTitle>Company Information</S.TextTitle>
      </S.TextWrapper>
      <RadioGroup name="recruiterType" label="Your Hiring Needs" options={hiringNeedsOptions} />
      {hiringNeedsValue === 'representative' ? (
        <>
          <SearchAutocompleteField
            endpoint="/api/v1/companies/autocomplete"
            name="company"
            label="Company Name *"
            value={companyValue}
            setValue={setValue}
            marginbottom={16}
          />
        </>
      ) : null}
      <Button
        variant="contained"
        type="submit"
        text="Save"
        endIcon={isUpdatingRP ? <Spinner disabled={isUpdatingRP} variant="contained" /> : null}
        disabled={isUpdatingRP}
      />
    </>
  );
};

const RecruiterPreferences = () => {
  const recruiterPreferencesData = useCS(
    ProfileConfigContext,
    profileConfigSelectors.recruiterPreferencesData
  );

  const userData: any = useCS(GlobalConfigContext, globalConfigSelectors.userData);

  const { industry, jobTitle, recruiterType, activeCompany } = recruiterPreferencesData;

  const { isLoading } = useGetRecruiterPreferences();
  const { isLoading: isUpdatingRP, updateRecruiterPreferences } = useUpdateRecruiterPreferences();
  const { data: jobCategoryData, isLoading: isJobCategoryDataLoading } = useGetDropdownData(
    DropdownsEnum.JOB_CATEGORY,
    'Human resources'
  );

  // Fetch recruiter's company from memberships (§3 of docs: GET /memberships/me)
  const { data: membershipsData, isLoading: isLoadingMemberships } = useRequest({
    url: '/api/v1/memberships/me',
    method: ApiMethodsEnum.Get,
    sendOnStart: true,
  });

  const { send: createMembership } = useRequest({
    url: '/api/v1/memberships',
    method: ApiMethodsEnum.Post,
    sendOnStart: false,
    body: {},
  });

  // Prefer VERIFIED membership, fallback to first in list
  const membershipCompany = (() => {
    if (!Array.isArray(membershipsData) || membershipsData.length === 0) return null;
    const verified = membershipsData.find((m: any) => {
      return m.verificationStatus === 'VERIFIED';
    });
    return (verified || membershipsData[0])?.company || null;
  })();

  if (isLoading || isLoadingMemberships || !recruiterPreferencesData) return null;

  const defaultValues = {
    industry: industry || null,
    jobTitle: jobTitle || null,
    recruiterType: recruiterType || 'representative',
    // Default to the profile's activeCompany; fall back to the membership-derived company.
    company: activeCompany || membershipCompany || null,
  };

  const onSubmit = async (formData: IFormInput) => {
    const isRepresentative = formData.recruiterType === 'representative';

    // Ensure a membership exists for the chosen company before making it active.
    if (isRepresentative && formData.company) {
      const alreadyMember =
        Array.isArray(membershipsData) &&
        membershipsData.some((m: any) => {
          return m.company?.id === formData.company?.id;
        });
      if (!alreadyMember) {
        // New membership → pass the user's account email as workEmail so the verification
        // email is sent automatically (§3), same as the onboarding flow.
        await createMembership({
          company: { id: formData.company.id },
          role: 'INTERNAL_RECRUITER',
          workEmail: userData?.email,
        });
      }
    }

    // Set the selected company as the profile's activeCompany (representative only).
    const payload = {
      industry: formData.industry,
      jobTitle: formData.jobTitle,
      recruiterType: formData.recruiterType,
      activeCompanyId: isRepresentative && formData.company ? formData.company.id : null,
    };
    updateRecruiterPreferences(payload);
  };

  return (
    <>
      <FormContainer
        key={JSON.stringify(defaultValues)}
        defaultValues={defaultValues}
        onSuccess={onSubmit}
      >
        <RecruiterPreferencesFields
          isUpdatingRP={isUpdatingRP}
          jobCategoryData={jobCategoryData}
          isJobCategoryDataLoading={isJobCategoryDataLoading}
        />
      </FormContainer>
    </>
  );
};

export default RecruiterPreferences;
